<!--
  - Copyright (c) 2023-2024. Haodong JU
  -->

<template>
  <saa-s-child-page-container
    :use-return="true"
    @back="back"
  >
    <template #title>
      <div class="text-h3">
        {{ $t('OrderOverview') }}
      </div>
      <div class="text-body-2 text--secondary">
        {{ $t('AbonnementInfoCheckout') }}
      </div>
    </template>
    <v-card
      class="my-8 pa-2"
      elevation="0"
    >
      <v-card
        v-for="p in selectedItems"
        :key="p.id"
        class="d-flex align-center py-2 mb-2"
        color="transparent"
        elevation="0"
      >
        <div style="width: 50%">
          <div class="d-flex text-body-1">
            {{ p.name }}
            <div v-if="p.count">
              ×{{ p.count }}
            </div>
          </div>
          <div class="text-body-2 text--secondary">
            {{ p.description || $t('no_description') }}
          </div>
        </div>
        <v-spacer />
        <div
          class="d-block overflow-hidden"
        >
          <stripe-price-display
            :price="getCorrectPriceForProduct(p)"
            :real-count="p.count"
          />
        </div>
      </v-card>
    </v-card>
    <v-card
      class="pa-2 px-4 mb-2 d-flex align-center"
      color="grey lighten-3"
      elevation="0"
      @click="billedYearly=!billedYearly"
    >
      <div>
        <div class="text-body-1 font-weight-bold">
          {{ !billedYearly ? $t('BilledMonthly') : $t('BilledYearly') }}
        </div>
        <div class="text-body-2">
          {{ billedYearly ? $t('SwitchMonthlyPayment') : $t('SwitchYearlyPayment') }}
          <span
            v-if="!billedYearly"
            class="error--text font-weight-bold"
          >{{ totalMonthSubPrice * 12 - totalYearSubPrice | priceDisplay }}</span>
          <span
            v-else
            class="error--text font-weight-bold"
          >{{ totalMonthSubPrice - totalYearSubPrice / 12 | priceDisplay }}</span>
        </div>
      </div>
      <v-spacer />
      <v-icon
        v-if="billedYearly"
        class="mr-2"
      >
        mdi-checkbox-blank-outline
      </v-icon>
      <v-icon
        v-else
        class="mr-2"
      >
        mdi-checkbox-marked
      </v-icon>
    </v-card>
    <v-card
      class="pa-2 px-4 mb-2 d-flex align-center"
      color="grey lighten-3"
      elevation="0"
      @click="useTrial=!useTrial"
    >
      <div>
        <div class="text-body-1 ">
          {{ $t('HaveTrialCode') }}
        </div>
      </div>
      <v-spacer />
      <v-icon
        v-if="useTrial"
        class="mr-2"
      >
        mdi-checkbox-marked
      </v-icon>
      <v-icon
        v-else
        class="mr-2"
      >
        mdi-checkbox-blank-outline
      </v-icon>
    </v-card>
    <template #actions>
      <next-step-button
        :loading="loading"
        class="btnContentSelect"
        @next="next"
      >
        <div
          class="d-block overflow-hidden"
        >
          <div class="d-flex align-center justify-center">
            <div
              class="text-truncate"
              style="position: relative;"
            >
              {{ $t('ProceedCheckout') }}，{{ $t('TotalPayCheckout') }}
            </div>
            <div
              class="ml-1 text-no-wrap"
              style="position: relative;"
            >
              {{ totalSubPrice | priceDisplay }}/
            </div>
            <div
              class="text-truncate"
              style="position: relative;"
            >
              {{ billedMonthly ? $t('Monthly') : $t('Yearly') }}
            </div>
            <div
              class="text-truncate"
              style="position: relative;"
            >
              , {{ $t('AndOneTimePayment') }}
            </div>
            <div
              class="ml-1"
              style="position: relative;"
            >
              {{ totalOneTimePrice|priceDisplay }}
            </div>
          </div>
        </div>
      </next-step-button>
    </template>
  </saa-s-child-page-container>
</template>

<script>
import SaaSChildPageContainer from '@/views/SaaS/widget/SaaSChildPageContainer.vue'
import NextStepButton from '@/views/SaaS/widget/NextStepButton.vue'
import { getCurrentSaaSOrderInfo } from '@/views/SaaS/SaaSGuide'

import IKUtils from 'innerken-js-utils'
import StripePriceDisplay from '@/views/widget/commonLogic/StripePriceDisplay.vue'
import { loadProducts } from '@/model/dataLayer/api/SaaS/product'
import { checkTrialCode, doPayment } from '@/model/dataLayer/api/SaaS/SaaSUtils'

export default {
  name: 'CheckOutOverview',
  components: {
    StripePriceDisplay,
    NextStepButton,
    SaaSChildPageContainer
  },
  data: function () {
    return {
      selectedItems: [],
      useTrial: false,
      loading: false,
      billedYearly: true
    }
  },
  computed: {
    billedMonthly () {
      return !this.billedYearly
    },
    subItems () {
      return this.selectedItems.filter(it => it.realPrice.type !== 'one_time')
    },
    totalYearSubPrice () {
      return this.subItems.map(it => this.getCorrectPriceForProduct(it, false).unit_amount)
        .reduce(
          (a, b) => {
            return a + b
          }, 0) / 100 ?? 0
    },
    totalMonthSubPrice () {
      return this.subItems.map(it => this.getCorrectPriceForProduct(it, true).unit_amount)
        .reduce(
          (a, b) => {
            return a + b
          }, 0) / 100 ?? 0
    },
    totalOneTimePrice () {
      return (this.selectedItems.map(it => this.getCorrectPriceForProduct(it))
        .filter(x => x.type === 'one_time')
        .map(it => it.realTotalPrice)).reduce(
        (a, b) => {
          return a + b
        }, 0) / 100 ?? 0
    },
    totalSubPrice () {
      return this.subItems.map(it => this.getCorrectPriceForProduct(it))
        .map(it => it.unit_amount).reduce(
          (a, b) => {
            return a + b
          }, 0) / 100 ?? 0
    }
  },
  mounted () {
    this.selectedItems = getCurrentSaaSOrderInfo().selectedProductList
  },
  methods: {
    getCorrectPriceForProduct (product, overrideMonthly = null) {
      const isOneTime = product.prices.some(it => it.type === 'one_time')
      const monthly = overrideMonthly ?? this.billedMonthly
      if (isOneTime) {
        product.prices[0].count = product.count ?? 1
        product.prices[0].realTotalPrice = product.prices[0].count * product.prices[0].unit_amount
        return product.prices[0]
      } else {
        if (monthly) {
          return product.prices.find(it => it.interval === 'month')
        } else {
          return product.prices.find(it => it.interval !== 'month')
        }
      }
    },
    back () {
      this.$router.push({ name: 'SaaS' })
    },
    async next () {
      this.loading = true
      const enProducts = await loadProducts(1)
      const needToBuyProductCode = getCurrentSaaSOrderInfo().selectedProductList.map(it => it.metadata.code)
      const enNeedToBuyProductsList = enProducts.filter(it => needToBuyProductCode.includes(it.metadata.code))
      enNeedToBuyProductsList.forEach(it => {
        it.realPrice = this.getCorrectPriceForProduct(it)
      })
      if (this.useTrial) {
        const codeList = enNeedToBuyProductsList.map(it => it.metadata.code)
        const trialDate = await checkTrialCode(codeList)
        if (trialDate) {
          IKUtils.showLoading(true)
          const result = await doPayment(
            enNeedToBuyProductsList,
            getCurrentSaaSOrderInfo().storeId,
            trialDate)
          if (result) {
            location.reload()
          }
        } else {
          IKUtils.showError(this.$t('WrongTrialCode'))
        }
      } else {
        const result = await doPayment(
          enNeedToBuyProductsList,
          getCurrentSaaSOrderInfo().storeId)
        if (result) {
          location.reload()
        }
        console.log(result, 'payment result')
      }
      this.loading = false
    }

  }
}
</script>

<style scoped>

</style>
